<template>
  <div id="schedule">
    <div id="view-sub-nav">
      <v-btn v-show="!resourceMode" icon size="small" color="accent" class="elevation-0" @click="newTaskDialog = true">
        <v-icon>mdi-plus</v-icon>
      </v-btn>
      <v-btn
        v-show="!resourceMode"
        icon
        size="small"
        color="info"
        class="elevation-0 ml-2"
        @click="repeatTaskDialog = true"
      >
        <v-icon>mdi-repeat</v-icon>
      </v-btn>
      <v-btn
        v-show="!resourceMode"
        icon
        size="small"
        color="primary"
        class="elevation-0 ml-2"
        @click="showUnscheduled = !showUnscheduled"
      >
        {{ unscheduledByShipment.length }}
      </v-btn>
      <v-btn @click="resetPagination" variant="outlined" class="ml-2">Today</v-btn>
      <v-icon class="ml-2" @click="paginateCalendar('prev')">mdi-chevron-left</v-icon>
      <v-icon class="ml-2 mr-3" @click="paginateCalendar('next')">mdi-chevron-right</v-icon>
      <div class="activeRangeString text-h5">
        {{ activeRangeString }}
      </div>
      <v-spacer></v-spacer>
      <v-tooltip v-if="resourceMode && !resetPreviewMode && !holidayPreviewMode" location="bottom">
        <template v-slot:activator="{ props }">
          <v-btn
            icon
            size="x-small"
            color="info"
            class="mr-2 elevation-0"
            v-bind="props"
            @click="updateHolidays"
            :loading="holidaysLoading"
          >
            <v-icon>mdi-cloud-download</v-icon>
          </v-btn>
        </template>
        <span>Reset Holiday from Timetastic</span>
      </v-tooltip>
      <v-tooltip v-if="resourceMode && !resetPreviewMode && !holidayPreviewMode" location="bottom">
        <template v-slot:activator="{ props }">
          <v-btn
            icon
            size="x-small"
            color="primary"
            class="mr-4 elevation-0"
            @click="resetPreviewMode = true"
            v-bind="props"
          >
            <v-icon>mdi-briefcase-download</v-icon>
          </v-btn>
        </template>
        <span>Reset from Staff Settings</span>
      </v-tooltip>
      <template v-if="resourceMode && resetPreviewMode">
        <v-btn size="small" color="success" class="mr-2 elevation-0" @click="saveReset"> SAVE RESET </v-btn>
        <v-btn size="small" color="error" class="mr-4 elevation-0" @click="resetPreviewMode = false">
          CANCEL RESET
        </v-btn>
      </template>
      <template v-if="resourceMode && holidayPreviewMode">
        <v-btn size="small" color="success" class="mr-2 elevation-0" @click="saveHolidayUpdate"> SAVE HOLIDAYS </v-btn>
        <v-btn
          size="small"
          color="error"
          class="mr-4 elevation-0"
          @click="
            () => {
              holidayPreviewMode = false
              timetasticHolidays = null
            }
          "
        >
          CANCEL HOLIDAYS
        </v-btn>
      </template>
      <v-switch
        hide-details
        v-if="selectedCalendarView === 'custom-daily'"
        class="mr-6 mt-0"
        small
        v-model="resourceMode"
        label="View Resources"
      ></v-switch>
      <v-select
        v-model="selectedCalendarView"
        :items="calendarViews"
        hide-details
        class="pt-0 mt-0 ml-2 view-select"
        @update:model-value="onRangeChange"
      >
      </v-select>
    </div>

    <v-progress-linear v-if="!tasksLoaded || !capacityLoaded" indeterminate></v-progress-linear>

    <div v-show="tasksLoaded" id="schedule-body">
      <div ref="unscheduledContainer" id="unscheduled-container" :class="{ open: showUnscheduled }">
        <v-expansion-panels style="width: 100%">
          <v-expansion-panel>
            <v-expansion-panel-title style="width: 100%">
              <div class="py-2 d-flex align-center" style="width: 100%">
                <div class="flex-grow-1">
                  <!-- Top row: Count Chip -->
                  <div class="d-flex justify-space-between align-center">
                    <div class="text-caption font-weight-bold ml-2">Manual Tasks</div>
                    <div>
                      <v-chip class="p-0 text-caption" size="small" variant="outlined">
                        {{ manualTasks.length }} Tasks
                      </v-chip>
                    </div>
                  </div>
                </div>
              </div>
            </v-expansion-panel-title>
            <v-expansion-panel-text>
              <draggable
                v-model="manualTasks"
                :group="{ name: 'unscheduled-tasks', pull: true, put: false }"
                @end="onSortEnd"
                class="draggable-list"
                :sort="false"
                id="unscheduled"
                item-key="id"
              >
                <template #item="{ element: task }">
                  <div
                    class="task text-body-2 justify-space-between"
                    :class="[task.type, $utils.camelize(task.subtype), task.paused ? 'paused' : 'coloured']"
                    :id="task.id"
                  >
                    <div class="type">
                      {{ task.objectType }}
                      {{ task.subtype ? task.subtype : task.type }}
                    </div>
                    <div class="count">
                      {{ task.units }}
                      <v-icon size="small" @click="openTask(task)">mdi-pencil</v-icon>
                    </div>
                  </div>
                </template>
              </draggable>
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>

        <!-- Processing Card -->
        <v-card border class="processing-card">
          <v-card-title class="py-2 text-caption font-weight-bold bg-grey-lighten-2"> Processing </v-card-title>
          <v-card-text class="pa-0">
            <v-expansion-panels style="width: 100%">
              <draggable
                v-model="processingShipments"
                :group="{ name: 'shipments', pull: true, put: ['shipments'] }"
                handle=".drag-handle"
                @end="onShipmentDragEnd"
                style="width: 100%"
                :item-key="shipmentGroupKey"
              >
                <template #item="{ element: tasks, index }">
                  <v-expansion-panel class="shipment-panel" style="width: 100%">
                    <v-expansion-panel-title :class="unscheduledShipmentColour(tasks)" style="width: 100%">
                      <div class="py-4 d-flex align-center" v-if="tasks.length > 0 && tasksBounded" style="width: 100%">
                        <v-icon class="drag-handle mr-2" size="small" style="cursor: grab">mdi-drag</v-icon>
                        <div class="flex-grow-1">
                          <!-- Top row: Icons and Date Chip -->
                          <div class="d-flex justify-space-between align-center mb-1">
                            <div>
                              <v-icon
                                v-if="
                                  tasks[0].shipmentID &&
                                  isShipmentPropertyTrue(tasks[0].shipmentID, 'balanceOutstanding')
                                "
                                size="small"
                                class="mr-1"
                                >mdi-truck</v-icon
                              >
                              <v-icon
                                v-if="!isTaskPropertyTrue(tasks[0].shipmentID, 'materialOrdered')"
                                size="small"
                                class="mr-1 pb-1"
                                >mdi-cart</v-icon
                              >
                              <v-icon
                                v-if="!isTaskPropertyTrue(tasks[0].shipmentID, 'toolpathed')"
                                size="small"
                                class="mr-1 pb-1"
                                >mdi-desktop-classic</v-icon
                              >
                            </div>
                            <div>
                              <v-chip
                                class="p-0 text-caption"
                                v-if="tasks[0].targetDeliveryDate && !tasks[0].repeatTaskId"
                                size="small"
                                variant="outlined"
                              >
                                WK{{ $utils.momentFromTimestamp(tasks[0].targetDeliveryDate).week() }} -
                                {{ $utils.momentFromTimestamp(tasks[0].targetDeliveryDate).format('DD/MMM') }}
                              </v-chip>
                              <v-chip
                                class="p-0 text-caption"
                                v-else-if="!tasks[0].targetDeliveryDate && tasks[0].repeatTaskId"
                                size="small"
                                variant="outlined"
                              >
                                Repeat Task
                              </v-chip>
                              <v-chip class="p-0 text-caption" v-else size="small" variant="outlined"> ASAP </v-chip>
                            </div>
                          </div>
                          <div class="d-flex justify-space-between align-center">
                            <div class="d-flex align-center text-truncate mr-2">
                              <v-icon v-if="tasks[0].recutOrderNumber" size="small" class="mr-1"
                                >mdi-content-cut</v-icon
                              >
                              {{ tasks[0].orderNumber || tasks[0].invoiceNumber }}
                            </div>
                            <div class="text-truncate">
                              {{ tasks[0].customerEmail }}
                            </div>
                          </div>
                        </div>
                        <v-icon size="18" class="ml-3 info-icon" @click.stop="openShipmentInfo(tasks)"
                          >mdi-information</v-icon
                        >
                      </div>
                    </v-expansion-panel-title>
                    <v-expansion-panel-text>
                      <draggable
                        v-model="processingShipments[index]"
                        :group="{ name: 'unscheduled-tasks', pull: true, put: false }"
                        @end="onSortEnd"
                        class="draggable-list"
                        :sort="false"
                        id="unscheduled"
                        item-key="id"
                      >
                        <template #item="{ element: task }">
                          <div
                            class="task text-body-2 justify-space-between"
                            :class="[task.type, $utils.camelize(task.subtype), task.paused ? 'paused' : 'coloured']"
                            :id="task.id"
                          >
                            <div class="type">
                              {{ task.objectType }}
                              {{ task.subtype ? task.subtype : task.type }}
                            </div>
                            <div class="count">
                              {{ task.units }}
                              <v-icon size="small" @click="openTask(task)">mdi-pencil</v-icon>
                            </div>
                          </div>
                        </template>
                      </draggable>
                    </v-expansion-panel-text>
                  </v-expansion-panel>
                </template>
              </draggable>
            </v-expansion-panels>
          </v-card-text>
        </v-card>

        <!-- Jobs to Schedule Card -->
        <v-card border class="jobs-to-schedule-card">
          <v-card-title class="py-2 text-caption font-weight-bold bg-grey-lighten-2"> Jobs to Schedule </v-card-title>
          <v-card-text class="pa-0">
            <v-expansion-panels style="width: 100%">
              <draggable
                v-model="jobsToScheduleShipments"
                :group="{ name: 'shipments', pull: true, put: ['shipments'] }"
                handle=".drag-handle"
                @end="onShipmentDragEnd"
                style="width: 100%"
                :item-key="shipmentGroupKey"
              >
                <template #item="{ element: tasks, index }">
                  <v-expansion-panel class="shipment-panel" style="width: 100%">
                    <v-expansion-panel-title :class="unscheduledShipmentColour(tasks)" style="width: 100%">
                      <div class="py-4 d-flex align-center" v-if="tasks.length > 0 && tasksBounded" style="width: 100%">
                        <v-icon class="drag-handle mr-2" size="small" style="cursor: grab">mdi-drag</v-icon>
                        <div class="flex-grow-1">
                          <!-- Top row: Icons and Date Chip -->
                          <div class="d-flex justify-space-between align-center mb-1">
                            <div>
                              <v-icon
                                v-if="
                                  tasks[0].shipmentID &&
                                  isShipmentPropertyTrue(tasks[0].shipmentID, 'balanceOutstanding')
                                "
                                size="small"
                                class="mr-1"
                                >mdi-truck</v-icon
                              >
                              <v-icon
                                v-if="!isTaskPropertyTrue(tasks[0].shipmentID, 'materialOrdered')"
                                size="small"
                                class="mr-1 pb-1"
                                >mdi-cart</v-icon
                              >
                              <v-icon
                                v-if="!isTaskPropertyTrue(tasks[0].shipmentID, 'toolpathed')"
                                size="small"
                                class="mr-1 pb-1"
                                >mdi-desktop-classic</v-icon
                              >
                            </div>
                            <div>
                              <v-chip
                                class="p-0 text-caption"
                                v-if="tasks[0].targetDeliveryDate && !tasks[0].repeatTaskId"
                                size="small"
                                variant="outlined"
                              >
                                WK{{ $utils.momentFromTimestamp(tasks[0].targetDeliveryDate).week() }} -
                                {{ $utils.momentFromTimestamp(tasks[0].targetDeliveryDate).format('DD/MMM') }}
                              </v-chip>
                              <v-chip
                                class="p-0 text-caption"
                                v-else-if="!tasks[0].targetDeliveryDate && tasks[0].repeatTaskId"
                                size="small"
                                variant="outlined"
                              >
                                Repeat Task
                              </v-chip>
                              <v-chip class="p-0 text-caption" v-else size="small" variant="outlined"> ASAP </v-chip>
                            </div>
                          </div>
                          <div class="d-flex justify-space-between align-center">
                            <div class="d-flex align-center text-truncate mr-2">
                              <v-icon v-if="tasks[0].recutOrderNumber" size="small" class="mr-1"
                                >mdi-content-cut</v-icon
                              >
                              {{ tasks[0].orderNumber || tasks[0].invoiceNumber }}
                            </div>
                            <div class="text-truncate">
                              {{ tasks[0].customerEmail }}
                            </div>
                          </div>
                        </div>
                        <v-icon size="18" class="ml-3 info-icon" @click.stop="openShipmentInfo(tasks)"
                          >mdi-information</v-icon
                        >
                      </div>
                    </v-expansion-panel-title>
                    <v-expansion-panel-text>
                      <draggable
                        v-model="jobsToScheduleShipments[index]"
                        :group="{ name: 'unscheduled-tasks', pull: true, put: false }"
                        @end="onSortEnd"
                        class="draggable-list"
                        :sort="false"
                        id="unscheduled"
                        item-key="id"
                      >
                        <template #item="{ element: task }">
                          <div
                            class="task text-body-2 justify-space-between"
                            :class="[task.type, $utils.camelize(task.subtype), task.paused ? 'paused' : 'coloured']"
                            :id="task.id"
                          >
                            <div class="type">
                              {{ task.objectType }}
                              {{ task.subtype ? task.subtype : task.type }}
                            </div>
                            <div class="count">
                              {{ task.units }}
                              <v-icon size="small" @click="openTask(task)">mdi-pencil</v-icon>
                            </div>
                          </div>
                        </template>
                      </draggable>
                    </v-expansion-panel-text>
                  </v-expansion-panel>
                </template>
              </draggable>
            </v-expansion-panels>
          </v-card-text>
        </v-card>

        <!-- Ready for Dispatch Card -->
        <v-card border class="mb-2 ready-for-dispatch-card">
          <v-card-title class="py-2 text-caption font-weight-bold bg-grey-lighten-2"> Ready for Dispatch </v-card-title>
          <v-card-text class="pa-0">
            <v-expansion-panels style="width: 100%">
              <draggable
                v-model="readyForDispatchShipments"
                :group="{ name: 'shipments', pull: true, put: ['shipments'] }"
                handle=".drag-handle"
                @end="onShipmentDragEnd"
                style="width: 100%"
                :item-key="shipmentGroupKey"
              >
                <template #item="{ element: tasks, index }">
                  <v-expansion-panel class="shipment-panel" style="width: 100%">
                    <v-expansion-panel-title :class="unscheduledShipmentColour(tasks)" style="width: 100%">
                      <div class="py-4 d-flex align-center" v-if="tasks.length > 0 && tasksBounded" style="width: 100%">
                        <v-icon class="drag-handle mr-2" size="small" style="cursor: grab">mdi-drag</v-icon>
                        <div class="flex-grow-1">
                          <!-- Top row: Icons and Date Chip -->
                          <div class="d-flex justify-space-between align-center mb-1">
                            <div>
                              <v-icon
                                v-if="
                                  tasks[0].shipmentID &&
                                  isShipmentPropertyTrue(tasks[0].shipmentID, 'balanceOutstanding')
                                "
                                size="small"
                                class="mr-1"
                                >mdi-truck</v-icon
                              >
                              <v-icon
                                v-if="!isTaskPropertyTrue(tasks[0].shipmentID, 'materialOrdered')"
                                size="small"
                                class="mr-1 pb-1"
                                >mdi-cart</v-icon
                              >
                              <v-icon
                                v-if="!isTaskPropertyTrue(tasks[0].shipmentID, 'toolpathed')"
                                size="small"
                                class="mr-1 pb-1"
                                >mdi-desktop-classic</v-icon
                              >
                            </div>
                            <div>
                              <v-chip
                                class="p-0 text-caption"
                                v-if="tasks[0].targetDeliveryDate && !tasks[0].repeatTaskId"
                                size="small"
                                variant="outlined"
                              >
                                WK{{ $utils.momentFromTimestamp(tasks[0].targetDeliveryDate).week() }} -
                                {{ $utils.momentFromTimestamp(tasks[0].targetDeliveryDate).format('DD/MMM') }}
                              </v-chip>
                              <v-chip
                                class="p-0 text-caption"
                                v-else-if="!tasks[0].targetDeliveryDate && tasks[0].repeatTaskId"
                                size="small"
                                variant="outlined"
                              >
                                Repeat Task
                              </v-chip>
                              <v-chip class="p-0 text-caption" v-else size="small" variant="outlined"> ASAP </v-chip>
                            </div>
                          </div>
                          <div class="d-flex justify-space-between align-center">
                            <div class="d-flex align-center text-truncate mr-2">
                              <v-icon v-if="tasks[0].recutOrderNumber" size="small" class="mr-1"
                                >mdi-content-cut</v-icon
                              >
                              {{ tasks[0].orderNumber || tasks[0].invoiceNumber }}
                            </div>
                            <div class="text-truncate">
                              {{ tasks[0].customerEmail }}
                            </div>
                          </div>
                        </div>
                        <v-icon size="18" class="ml-3 info-icon" @click.stop="openShipmentInfo(tasks)"
                          >mdi-information</v-icon
                        >
                      </div>
                    </v-expansion-panel-title>
                    <v-expansion-panel-text>
                      <draggable
                        v-model="readyForDispatchShipments[index]"
                        :group="{ name: 'unscheduled-tasks', pull: true, put: false }"
                        @end="onSortEnd"
                        class="draggable-list"
                        :sort="false"
                        id="unscheduled"
                        item-key="id"
                      >
                        <template #item="{ element: task }">
                          <div
                            class="task text-body-2 justify-space-between"
                            :class="[task.type, $utils.camelize(task.subtype), task.paused ? 'paused' : 'coloured']"
                            :id="task.id"
                          >
                            <div class="type">
                              {{ task.objectType }}
                              {{ task.subtype ? task.subtype : task.type }}
                            </div>
                            <div class="count">
                              {{ task.units }}
                              <v-icon size="small" @click="openTask(task)">mdi-pencil</v-icon>
                            </div>
                          </div>
                        </template>
                      </draggable>
                    </v-expansion-panel-text>
                  </v-expansion-panel>
                </template>
              </draggable>
            </v-expansion-panels>
          </v-card-text>
        </v-card>

        <draggable
          :list="[]"
          item-key="id"
          :group="{ name: 'unscheduled-tasks', pull: true, put: false }"
          @end="onSortEnd"
          id="unscheduled"
          class="draggable-list"
        >
          <template #item="{ element }"><span :data-task-id="element.id"></span></template>
        </draggable>

        <!-- Small dropzone for tasks -->
        <div class="dropzone-container">
          <draggable
            v-model="dropzoneTasks"
            item-key="id"
            :group="{ name: 'dropzone', pull: true, put: ['scheduled-tasks', 'unscheduled-tasks'] }"
            @end="onSortEnd"
            @add="onSortEnd"
            class="small-dropzone"
            :sort="false"
            id="dropzone"
          >
            <template #header>
              <div class="dropzone-content">
                <v-icon size="small" class="mb-1">mdi-plus</v-icon>
                <div class="text-caption">Drop tasks here</div>
              </div>
            </template>
            <template #item="{ element }"><span :data-task-id="element.id"></span></template>
          </draggable>
        </div>
      </div>

      <!-- COMPONENT -->
      <div id="calendar-container">
        <transition name="fade-transition" mode="out-in">
          <component
            ref="activeView"
            v-if="tasksLoaded && capacityLoaded"
            :is="activeComponent"
            :taskTypeGroups="taskTypeGroups"
            :today="today"
            v-model:tasksByDay="tasksByDay"
            v-model:capacityObject="capacityObject"
            :focussedTask="focussedTask"
            :showUnscheduled="showUnscheduled"
            :isShipmentPropertyTrue="isShipmentPropertyTrue"
            :resetPreviewMode="resetPreviewMode"
            :holidayPreviewMode="holidayPreviewMode"
            :timetasticHolidays="timetasticHolidays"
            @onSortEnd="onSortEnd"
            @openTask="openTask"
            @focusTask="focusTask"
            @resourcesChanged="resourcesChanged"
            @cutHoursChanged="cutHoursChanged"
            @resetApplied="resetPreviewMode = false"
            @holidayPreviewApplied="
              () => {
                holidayPreviewMode = false
                timetasticHolidays = null
              }
            "
          >
          </component>
        </transition>
      </div>
    </div>

    <!-- DIALOGS -->
    <v-dialog v-model="taskDialog" width="1000" content-class="task-dialog" @update:model-value="onTaskDialogClose">
      <v-card v-if="selectedTask">
        <v-card-title
          class="text-white text-h6 coloured"
          :class="[selectedTask.type, $utils.camelize(selectedTask.subtype)]"
        >
          {{ selectedTask.customerEmail }}
          {{ selectedTask.invoiceNumber || selectedTask.orderNumber }}
          {{ selectedTask.objectType }} {{ selectedTask.subtype }}
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col md="6">
              <h3 class="my-4">Notes</h3>
              <v-textarea
                class="mb-3"
                hide-details
                placeholder="Tap to add notes"
                variant="outlined"
                v-model="selectedTask.notes"
              ></v-textarea>
              <div v-if="orderForSelectedTask && selectedTask.type === 'dispatch' && !selectedTask.repeatTaskId">
                <h3 class="my-4">Useful Info</h3>
                <div>
                  <span class="text-body-2">Delivery Postcode</span>
                  <span class="ml-4 text-body-1">{{
                    orderForSelectedTask.customerContactDetails.deliveryPostcode
                  }}</span>
                </div>
                <div>
                  <span class="text-body-2">Customer Name</span>
                  <span class="ml-4 text-body-1"
                    >{{ orderForSelectedTask.customerContactDetails.firstName }}
                    {{ orderForSelectedTask.customerContactDetails.lastName }}</span
                  >
                </div>
                <div>
                  <span class="text-body-2">Customer Phone</span>
                  <span class="ml-4 text-body-1">{{
                    orderForSelectedTask.customerContactDetails.mobile ||
                    orderForSelectedTask.customerContactDetails.phone1
                  }}</span>
                </div>
                <div v-if="this.selectedTask.recutOrderNumber">
                  <h3 class="my-4">Order has recut</h3>
                  <p>
                    Shipment must contain recut from order number
                    {{ this.selectedTask.recutOrderNumber }}
                  </p>
                </div>
              </div>
            </v-col>
            <v-spacer />
            <v-col cols="4" v-if="!displayDispatchEtaPicker">
              <h3 class="my-4">Actions</h3>
              <v-btn
                class="my-2"
                v-if="selectedTask.dateScheduled == null"
                color="warning"
                block
                variant="outlined"
                @click="deleteSelectedTask"
                ><v-icon size="small" class="mr-2">mdi-warning</v-icon>Delete Task</v-btn
              >
              <v-btn
                v-if="!selectedTask.repeatTaskId"
                class="my-2"
                color="primary"
                block
                variant="outlined"
                @click="openSprayerView(selectedTask.shipmentID)"
                ><v-icon size="small" class="mr-2">mdi-spray</v-icon>Open Sprayer List</v-btn
              >
              <v-btn
                v-if="!selectedTask.repeatTaskId"
                class="my-2"
                color="primary"
                block
                variant="outlined"
                @click="openPackingView(selectedTask.shipmentID)"
                ><v-icon size="small" class="mr-2">mdi-clipboard-check</v-icon>Open Packing List</v-btn
              >
              <v-btn
                v-if="!selectedTask.repeatTaskId"
                class="my-2"
                color="primary"
                block
                variant="outlined"
                @click="printableSVG(selectedTask.shipmentID)"
                ><v-icon size="small" class="mr-2">mdi-chart-tree</v-icon>Open Visual</v-btn
              >
              <v-btn
                v-if="selectedTask.type === 'dispatch' && selectedTask.subtype === 'To customer'"
                class="my-2"
                color="primary"
                block
                variant="outlined"
                @click="toggleCourierBookedStatus"
                ><v-icon
                  size="small"
                  class="mr-2"
                  :color="isShipmentPropertyTrue(selectedTask.shipmentID, 'balanceOutstanding') ? 'orange' : 'primary'"
                  >mdi-truck-check</v-icon
                >
                {{ selectedTask.courierBooked ? 'Courier Not Booked' : 'Courier Booked' }}
              </v-btn>
              <v-btn
                v-if="selectedTask.type === 'dispatch' && selectedTask.subtype === 'To customer'"
                class="my-2"
                color="primary"
                block
                variant="outlined"
                @click="displayDispatchEtaPicker = true"
                ><v-icon size="small" class="mr-2">mdi-clock-time-four</v-icon>Set Dispatch ETA</v-btn
              >
              <v-btn
                v-if="selectedTask.siblingId && !selectedTask.repeatTaskId"
                class="my-2"
                color="primary"
                block
                variant="outlined"
                @click="unsplitTask"
                ><v-icon size="small" class="mr-2">mdi-set-center</v-icon>Unsplit Task</v-btn
              >
              <div v-else-if="!selectedTask.siblingId && !selectedTask.repeatTaskId">
                <v-btn
                  v-if="!splitDialog"
                  class="my-2"
                  color="primary"
                  block
                  variant="outlined"
                  @click="
                    () => {
                      splitDialog = true
                      splitVal = Math.round(selectedTask.units / 2)
                    }
                  "
                  ><v-icon size="small" class="mr-2">mdi-arrow-split-vertical</v-icon>Split Task</v-btn
                >
                <div class="d-flex align-center" v-else :style="{ height: '36px' }">
                  <div class="text-center" :style="{ width: '20px' }">{{ selectedTask.units - splitVal }}</div>
                  <v-slider
                    v-model="splitVal"
                    :min="1"
                    :max="selectedTask.units - 1"
                    :step="1"
                    hide-details
                    class="mx-1"
                  ></v-slider>
                  <div class="text-center" :style="{ width: '20px' }">{{ splitVal }}</div>
                  <v-btn
                    color="primary"
                    variant="outlined"
                    icon
                    size="x-small"
                    class="ml-2"
                    @click="
                      () => {
                        splitDialog = false
                      }
                    "
                    :disabled="splittingProcess"
                    ><v-icon>mdi-close</v-icon></v-btn
                  >
                  <v-btn
                    color="primary"
                    variant="outlined"
                    icon
                    size="x-small"
                    class="ml-2"
                    @click="splitTask"
                    :disabled="splittingProcess"
                    ><v-icon>mdi-arrow-split-vertical</v-icon></v-btn
                  >
                </div>
              </div>
              <v-btn
                class="mt-8"
                v-if="!selectedTask.complete && !isEuDispatchToCustomer"
                color="accent"
                block
                variant="flat"
                :disabled="
                  selectedTask.type === 'dispatch' &&
                  selectedTask.subtype === 'To customer' &&
                  !selectedTask.dispatchEta &&
                  !tmpOverideDispatchEta
                "
                @click="completeSelectedTask"
                >Task Complete 🙌</v-btn
              >
              <v-alert
                v-if="!selectedTask.complete && isEuDispatchToCustomer"
                class="mt-8"
                type="info"
                variant="outlined"
                density="compact"
              >
                EU shipments must be dispatched from the Shipments view (Mark as dispatched).
              </v-alert>
              <v-btn
                class="mt-8"
                v-if="selectedTask.complete"
                color="accent"
                block
                variant="flat"
                @click="uncompleteTask"
                >Task Not Complete</v-btn
              >
            </v-col>
            <v-col cols="4" v-else>
              <h3 class="my-4">Dispatch ETA</h3>
              <v-btn
                class="mb-4"
                @click="
                  () => {
                    tmpOverideDispatchEta = true
                    displayDispatchEtaPicker = false
                  }
                "
                block
                variant="flat"
                >Unavailable</v-btn
              >
              <v-time-picker v-model="selectedTask.dispatchEta" format="24hr" full-width></v-time-picker>
              <v-btn class="mt-8" color="accent" block variant="flat" @click="updateDispatchETA">Done</v-btn>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="newTaskDialog" width="500">
      <v-card>
        <v-card-title class="bg-primary text-white text-h6">Add Task</v-card-title>
        <v-card-text>
          <v-form ref="form" validate-on="blur">
            <v-autocomplete
              :items="taskTypes"
              v-model="form.type"
              label="Type"
              :rules="[$rules.required('task type')]"
              required
            ></v-autocomplete>
            <v-autocomplete
              v-if="form.type && taskSubTypes[form.type]"
              :items="taskSubTypes[form.type]"
              v-model="form.subtype"
              label="Sub Type"
              :rules="[$rules.required('task sub type')]"
              required
            ></v-autocomplete>
            <v-text-field
              v-model="form.customerEmail"
              label="Customer Email"
              :rules="[$rules.required('email'), $rules.email('email')]"
              required
            ></v-text-field>
            <v-text-field
              v-model="form.invoiceNumber"
              label="Invoice Number"
              :rules="[$rules.required('invoice number')]"
              required
            ></v-text-field>
            <v-text-field
              v-model="form.units"
              label="Units"
              suffix="Quantity Units"
              :rules="[$rules.required('units')]"
              type="number"
              required
            ></v-text-field>
            <v-text-field
              v-model="form.workMinutes"
              label="Work Minutes Per Head"
              :rules="[$rules.required('work minutes')]"
              type="number"
              required
            ></v-text-field>
            <v-text-field
              v-model="form.mu"
              disabled
              label="MegaUnits"
              suffix="Mµ (per head)"
              :rules="[$rules.required('mega units')]"
              required
            ></v-text-field>
            <v-text-field
              v-model="form.objectType"
              label="Label"
              :rules="[$rules.required('label')]"
              required
            ></v-text-field>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-btn
            color="primary"
            variant="text"
            @click="
              () => {
                $refs.form.reset(), (newTaskDialog = false)
              }
            "
            >Cancel</v-btn
          >
          <v-spacer></v-spacer>
          <v-btn :disabled="submitting" color="accent" variant="flat" @click="validate">Create</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Repeat Task Dialog -->
    <RepeatTaskDialog v-model="repeatTaskDialog" />

    <!-- Calculation Dialog -->
    <v-dialog v-model="calculationDialog" @click:outside="calculationDialog = false">
      <mu-calculator
        v-if="tasksLoaded && capacityLoaded"
        :tasks-by-day="tasksByDay"
        :capacity-object="capacityObject"
      />
    </v-dialog>

    <!-- Shipment Info Dialog -->
    <v-dialog v-model="shipmentInfoDialog" width="600">
      <v-card v-if="selectedShipmentInfo">
        <v-card-title class="bg-primary text-white text-h6">
          {{ selectedShipmentInfo[0].orderNumber || selectedShipmentInfo[0].invoiceNumber }}
          — {{ selectedShipmentInfo[0].customerEmail }}
        </v-card-title>
        <v-card-text>
          <div class="mt-4 mb-4">
            <span class="text-body-2 font-weight-bold">Material Due In:</span>
            <span class="ml-2">{{ getShipmentMaterialDueIn(selectedShipmentInfo[0].shipmentID) }}</span>
          </div>
          <v-btn
            class="my-2"
            color="primary"
            block
            variant="outlined"
            @click="openQuoteView(selectedShipmentInfo[0].shipmentID)"
          >
            <v-icon size="small" class="mr-2">mdi-clipboard-text</v-icon>Original Quote
          </v-btn>
          <v-btn
            class="my-2"
            color="primary"
            block
            variant="outlined"
            @click="openMaterialView(selectedShipmentInfo[0].shipmentID)"
          >
            <v-icon size="small" class="mr-2">mdi-cart</v-icon>Material Order List
          </v-btn>
          <v-btn
            class="my-2"
            color="primary"
            block
            variant="outlined"
            @click="openPackingView(selectedShipmentInfo[0].shipmentID)"
          >
            <v-icon size="small" class="mr-2">mdi-clipboard-check</v-icon>Packing List
          </v-btn>
          <v-btn
            class="my-2"
            color="primary"
            block
            variant="outlined"
            @click="openSprayerView(selectedShipmentInfo[0].shipmentID)"
          >
            <v-icon size="small" class="mr-2">mdi-spray</v-icon>Sprayer List
          </v-btn>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Material Order View Dialog -->
    <v-dialog v-model="materialViewDialog" fullscreen transition="dialog-bottom-transition">
      <v-card>
        <v-toolbar color="primary" class="no-print elevation-0">
          <v-toolbar-title>Material Order List</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn
            icon
            @click="
              () => {
                materialViewDialog = false
                materialViewShipment = null
              }
            "
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>
        <MaterialOrderView v-if="materialViewShipment" :shipment="materialViewShipment" />
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { markRaw } from 'vue'
import draggable from 'vuedraggable'
import Day from '@/components/schedule/Day'
import Week from '@/components/schedule/Week'
import Month from '@/components/schedule/Month'
import WeekResources from '@/components/schedule/WeekResources'
import MonthResources from '@/components/schedule/MonthResources'
import MuCalculator from '@/components/schedule/MuCalculator'
import RepeatTaskDialog from '@/components/schedule/RepeatTaskDialog'
import MaterialOrderView from '@/components/MaterialOrderView'
import { db, functions } from '@/js/db'
import { serializeSnapshot } from '@/js/live'
import {
  addDoc,
  collection,
  deleteDoc,
  deleteField,
  doc,
  documentId,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  setDoc,
  updateDoc,
  where,
  writeBatch
} from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'

export default {
  name: 'Schedule',
  components: {
    draggable,
    Day,
    Week,
    Month,
    WeekResources,
    MonthResources,
    MuCalculator,
    RepeatTaskDialog,
    MaterialOrderView
  },
  data() {
    return {
      today: this.$moment().format('YYYY-MM-DD'),
      calendarViews: [
        { value: 'day', text: 'Day', component: markRaw(Day) },
        { value: 'custom-daily', text: 'Week', component: markRaw(Week) },
        { value: 'month', text: 'Month', component: markRaw(Month) }
      ],
      resourceViews: [
        { value: 'custom-daily', text: 'Week', component: markRaw(WeekResources) },
        { value: 'month', text: 'Month', component: markRaw(MonthResources) }
      ],
      taskTypes: [
        { value: 'cut', text: 'Cut' },
        { value: 'finish', text: 'Finish' },
        { value: 'assemble', text: 'Assemble' },
        { value: 'pack', text: 'Pack' },
        { value: 'dispatch', text: 'Dispatch' }
      ],
      taskSubTypes: {
        dispatch: [
          { value: 'To customer', text: 'To Customer' },
          { value: 'Customer collection', text: 'Customer Collection' },
          { value: 'To sprayer', text: 'To Sprayer' }
        ]
      },
      taskTypeGroups: [
        {
          name: 'dispatch',
          types: ['dispatch'],
          label: 'Dispatch / Other'
        },
        { name: 'cut', types: ['cut'], label: 'Cut' },
        { name: 'finish', types: ['finish', 'assemble'], label: 'Finish' },
        { name: 'pack', types: ['pack'], label: 'Pack' }
      ],
      selectedCalendarView: 'day',
      tasksLoaded: false,
      capacityLoaded: false,
      tasks: [],
      tasksBounded: false,
      focussedTask: null,
      unscheduledByShipment: [],
      manualTasks: [],
      processingShipments: [],
      jobsToScheduleShipments: [],
      readyForDispatchShipments: [],
      dropzoneTasks: [],
      shipments: [],
      tasksByDay: [],
      unscheduledTasks: [],
      capacity: null,
      selectedTask: null,
      showUnscheduled: false,
      cuttingHourPerDay: 8,
      tmpValue: null,
      form: {},
      submitting: false,
      taskDialog: false,
      newTaskDialog: false,
      repeatTaskDialog: false,
      calculationDialog: false,
      resourceMode: false,
      displayDispatchEtaPicker: false,
      tmpOverideDispatchEta: false,
      orderForSelectedTask: null,
      splitDialog: false,
      splitVal: 0,
      splittingProcess: false,
      hasLoadedScheduleDashboard: false,
      shipmentInfoDialog: false,
      selectedShipmentInfo: null,
      materialViewDialog: false,
      materialViewShipment: null,
      resetPreviewMode: false,
      holidaysLoading: false,
      holidayPreviewMode: false,
      timetasticHolidays: null
    }
  },
  created() {
    this._shipmentChunkUnsubs = []
    this._shipmentChunks = []
    this._boundShipmentKey = null
  },
  async mounted() {
    this.loadTasks()
    this.loadUnscheduledTasks()
    this.updateCapacity()
    this.updateTasksByDay()
    // Load saved schedule dashboard order
    await this.loadScheduleDashboard()
  },
  beforeUnmount() {
    this._shipmentChunkUnsubs.forEach((unsub) => unsub())
    this._shipmentChunkUnsubs = []
  },
  computed: {
    shipmentsByID() {
      const map = {}
      for (const shipment of this.shipments) map[shipment.id] = shipment
      return map
    },
    capacityObject() {
      if (!this.capacity) return {}
      let o = {}
      for (const day of this.capacity) {
        // Create a shallow copy to avoid mutating the original reactive data
        const dayData = { ...day }
        if (dayData.cutting == null) {
          dayData.cutting = this.cuttingHourPerDay
        }
        o[this.$moment.unix(day.date.seconds).format('YYYY-MM-DD')] = dayData
      }
      return o
    },
    activeComponent() {
      return !this.resourceMode
        ? this.calendarViews.find((i) => this.selectedCalendarView === i.value).component
        : this.resourceViews.find((i) => this.selectedCalendarView === i.value).component
    },
    calendarDuration() {
      return this.$moment(this.activeDateRange.end).diff(this.$moment(this.activeDateRange.start), 'days') + 1
    },
    activeDateRange() {
      let r
      switch (this.selectedCalendarView) {
        case 'day':
          r = {
            start: this.today,
            end: this.today
          }
          break
        case 'custom-daily':
          r = {
            start: this.$moment(this.today).startOf('week').format('YYYY-MM-DD'),
            end: this.$moment(this.today).startOf('week').add(4, 'days').format('YYYY-MM-DD')
          }
          break
        case 'month':
          {
            const startWeekday = this.$moment(this.today).startOf('month').isoWeekday()
            const endWeekday = this.$moment(this.today).endOf('month').isoWeekday()

            const startAdjust = startWeekday < 6 ? 1 - startWeekday : 8 - startWeekday
            const endAdjust = 5 - endWeekday
            r = {
              start: this.$moment(this.today)
                .startOf('month')
                .add(startAdjust - 7, 'days')
                .format('YYYY-MM-DD'),
              end: this.$moment(this.today)
                .endOf('month')
                .add(endAdjust + 7, 'days')
                .format('YYYY-MM-DD')
            }
          }
          break
      }
      return r
    },
    activeRangeString() {
      var startMoment = this.$moment(this.activeDateRange.start)
      var endMoment = this.$moment(this.activeDateRange.end)
      let s

      switch (this.selectedCalendarView) {
        case 'day':
          s = this.$moment(this.today).format('dddd Do MMMM YYYY')
          break
        case 'month':
          s = this.$moment(this.today).format('MMMM YYYY')
          break
        case 'custom-daily':
          s =
            startMoment.month() == endMoment.month()
              ? this.$moment(this.today).format('MMMM YYYY [/ Week] ww')
              : startMoment.format('MMM') + ' - ' + endMoment.format('MMM YYYY [/ Week] ww')
          break
      }
      return s
    },
    isEuDispatchToCustomer() {
      return (
        this.selectedTask?.type === 'dispatch' &&
        this.selectedTask?.subtype === 'To customer' &&
        this.orderForSelectedTask?.region === 'eu'
      )
    },
    emptyShipmentGroupCount() {
      return [this.processingShipments, this.jobsToScheduleShipments, this.readyForDispatchShipments].reduce(
        (count, groups) => count + groups.filter((tasks) => !Array.isArray(tasks) || tasks.length === 0).length,
        0
      )
    }
  },
  watch: {
    unscheduledTasks: {
      handler() {
        this.updateUnscheduledByShipment()
      },
      deep: true
    },
    emptyShipmentGroupCount(count) {
      if (count > 0) this.removeEmptyShipments()
    },
    tasks: {
      handler() {
        this.updateTasksByDay()
      },
      deep: true
    },
    'form.workMinutes': function (val) {
      if (val) {
        this.form.mu = this.$utils.toFixedNumber((val * 60) / 120, 1)
      } else {
        this.form.mu = null
      }
    }
  },
  methods: {
    shipmentGroupKey(tasks) {
      return tasks && tasks.length ? tasks[0].shipmentID : 'empty'
    },
    onRangeChange() {
      this.resourceMode = false
      this.loadTasks()
      this.loadShipments()
      this.updateCapacity()
    },
    async openTask(task) {
      this.selectedTask = task
      this.tmpValue = task.notes
      this.taskDialog = true

      // load in the shipment so we can display stuff about it...
      this.orderForSelectedTask = null
      if (task.orderNumber) {
        const orderSnap = await getDocs(query(collection(db, 'orders'), where('orderNumber', '==', task.orderNumber)))
        this.orderForSelectedTask = orderSnap.docs[0].data()
      }
    },
    deleteSelectedTask() {
      this.taskDialog = false
      const taskIdToDelete = this.selectedTask.id

      deleteDoc(doc(db, 'tasks', taskIdToDelete))
        .then(() => {
          this.unscheduledTasks = this.unscheduledTasks.filter((task) => task.id !== taskIdToDelete)

          // Remove from manualTasks
          this.manualTasks = this.manualTasks.filter((task) => task.id !== taskIdToDelete)

          // Remove from shipment arrays
          this.removeTaskFromShipmentArrays(taskIdToDelete)

          this.selectedTask = null

          // Clean up empty shipments and save dashboard
          setTimeout(async () => {
            this.removeEmptyShipments()
            await this.saveScheduleDashboard()
          }, 100)
        })
        .catch((e) => {
          console.error('Error deleting task:', e)
          this.$store.commit('openSnackbar', {
            snackbarText: 'Error deleting task'
          })
        })
    },
    removeTaskFromShipmentArrays(taskId) {
      // Helper function to remove task from a shipment array
      const removeFromArray = (array) => {
        return array
          .map((shipment) => {
            if (Array.isArray(shipment)) {
              return shipment.filter((task) => task.id !== taskId)
            }
            return shipment
          })
          .filter((shipment) => Array.isArray(shipment) && shipment.length > 0)
      }

      this.processingShipments = removeFromArray(this.processingShipments)
      this.jobsToScheduleShipments = removeFromArray(this.jobsToScheduleShipments)
      this.readyForDispatchShipments = removeFromArray(this.readyForDispatchShipments)
    },
    uncompleteTask() {
      this.taskDialog = false
      updateDoc(doc(db, 'tasks', this.selectedTask.id), {
        complete: false
      })
        .then(() => {
          this.selectedTask = null
          this.updateTasksByDay()
        })
        .catch((e) => {
          console.log('error', e)
        })
    },
    toggleCourierBookedStatus() {
      this.taskDialog = false
      updateDoc(doc(db, 'tasks', this.selectedTask.id), {
        courierBooked: !this.selectedTask.courierBooked
      })
        .then(() => {
          this.selectedTask = null
          this.updateTasksByDay()
        })
        .catch((e) => {
          console.log('error', e)
        })
    },
    completeSelectedTask() {
      this.taskDialog = false

      updateDoc(doc(db, 'tasks', this.selectedTask.id), {
        complete: true
      })
        .then(() => {
          this.selectedTask = null
          this.updateTasksByDay()
        })
        .catch((e) => {
          console.log('error', e)
        })
    },
    updateDispatchETA() {
      updateDoc(doc(db, 'tasks', this.selectedTask.id), {
        dispatchEta: this.selectedTask.dispatchEta
      })
      updateDoc(doc(db, 'shipments', this.selectedTask.shipmentID), {
        dispatchEta: this.selectedTask.dispatchEta
      })

      this.displayDispatchEtaPicker = false
    },
    loadTasks() {
      this.tasksLoaded = false
      this.$live(
        'tasks',
        query(
          collection(db, 'tasks'),
          where('dateScheduled', '>=', this.$utils.timestampFromVuetify(this.activeDateRange.start)),
          where('dateScheduled', '<=', this.$utils.timestampFromVuetify(this.activeDateRange.end))
        )
      ).then(() => {
        this.tasksLoaded = true
        this.loadShipments()
      })
    },
    splitMu(total, units, part) {
      const snapshot = this.$utils.toFixedNumber(total, 1)
      const ceiled = Math.ceil(snapshot)
      const share = Math.ceil((ceiled / units) * part)
      return { snapshot, share, remainder: ceiled - share }
    },
    async splitTask() {
      this.splittingProcess = true
      let taskClone = { ...this.selectedTask }
      taskClone.siblingId = this.selectedTask.id
      const units = this.selectedTask.units
      const part = Math.round(this.splitVal)
      // find out which type of Mu Unit this task is
      switch (this.selectedTask.type) {
        case 'cut': {
          const mu = this.splitMu(this.selectedTask?.muCut, units, part)
          taskClone.originalMuCut = this.selectedTask?.muCut ? mu.snapshot : null
          taskClone.muCut = this.selectedTask?.muCut ? mu.share : null
          break
        }
        case 'finish':
        case 'assemble': {
          const mu = this.splitMu(this.selectedTask?.muFinish, units, part)
          taskClone.originalMuFinish = this.selectedTask?.muFinish ? mu.snapshot : null
          taskClone.muFinish = this.selectedTask?.muFinish ? mu.share : null
          break
        }
        case 'pack': {
          const mu = this.splitMu(this.selectedTask?.muPack, units, part)
          taskClone.originalMuPack = this.selectedTask?.muPack ? mu.snapshot : null
          taskClone.muPack = this.selectedTask?.muPack ? mu.share : null
          break
        }
        case 'dispatch': {
          const mu = this.splitMu(this.selectedTask?.muDispatch, units, part)
          taskClone.originalMuDispatch = this.selectedTask?.muDispatch ? mu.snapshot : null
          taskClone.muDispatch = this.selectedTask?.muDispatch ? mu.share : null
          break
        }
        default: {
          const mu = this.splitMu(this.selectedTask?.muUnits, units, part)
          taskClone.originalMuUnits = this.selectedTask?.muUnits ? mu.snapshot : null
          taskClone.muUnits = this.selectedTask?.muUnits ? mu.share : null
          break
        }
      }
      if (!taskClone.muUnits) delete taskClone.muUnits

      taskClone.originalUnits = this.selectedTask.units
      taskClone.shipmentRef = doc(db, 'shipments', this.selectedTask.shipmentID)
      taskClone.units = part
      delete taskClone.id

      const newTask = await addDoc(collection(db, 'tasks'), taskClone)
      const originalTask = doc(db, 'tasks', this.selectedTask.id)
      const taskUpdateInfo = {
        siblingId: newTask.id,
        originalUnits: this.selectedTask.units,
        units: this.selectedTask.units - part
      }

      // find out which type of Mu Unit this task is
      switch (this.selectedTask.type) {
        case 'cut': {
          const mu = this.splitMu(this.selectedTask?.muCut, units, part)
          taskUpdateInfo.originalMuCut = this.selectedTask?.muCut ? mu.snapshot : null
          taskUpdateInfo.muCut = this.selectedTask?.muCut ? mu.remainder : null
          break
        }
        case 'finish':
        case 'assemble': {
          const mu = this.splitMu(this.selectedTask?.muFinish, units, part)
          taskUpdateInfo.originalMuFinish = this.selectedTask?.muFinish ? mu.snapshot : null
          taskUpdateInfo.muFinish = this.selectedTask?.muFinish ? mu.remainder : null
          break
        }
        case 'pack': {
          const mu = this.splitMu(this.selectedTask?.muPack, units, part)
          taskUpdateInfo.originalMuPack = this.selectedTask?.muPack ? mu.snapshot : null
          taskUpdateInfo.muPack = this.selectedTask?.muPack ? mu.remainder : null
          break
        }
        case 'dispatch': {
          const mu = this.splitMu(this.selectedTask?.muDispatch, units, part)
          taskUpdateInfo.originalMuDispatch = this.selectedTask?.muDispatch ? mu.snapshot : null
          taskUpdateInfo.muDispatch = this.selectedTask?.muDispatch ? mu.remainder : null
          break
        }
        default: {
          const mu = this.splitMu(this.selectedTask?.muUnits, units, part)
          taskUpdateInfo.originalMuUnits = this.selectedTask?.muUnits ? mu.snapshot : null
          taskUpdateInfo.muUnits = this.selectedTask?.muUnits ? mu.remainder : null
          break
        }
      }
      if (!taskUpdateInfo.originalMuUnits) delete taskUpdateInfo.originalMuUnits
      if (!taskUpdateInfo.muUnits) delete taskUpdateInfo.muUnits
      await updateDoc(originalTask, taskUpdateInfo)

      this.splitDialog = false
      this.taskDialog = false
      this.splittingProcess = false
    },
    async unsplitTask() {
      await deleteDoc(doc(db, 'tasks', this.selectedTask.siblingId))

      let unsplitTaskUpdates = {
        units: this.selectedTask.originalUnits,
        siblingId: deleteField(),
        originalUnits: deleteField()
      }

      // find out which type of Mu Unit this task is
      switch (this.selectedTask.type) {
        case 'cut':
          unsplitTaskUpdates.muCut = this.selectedTask?.originalMuCut
            ? this.$utils.toFixedNumber(this.selectedTask.originalMuCut, 1)
            : null
          break
        case 'finish':
        case 'assemble':
          unsplitTaskUpdates.muFinish = this.selectedTask?.originalMuFinish
            ? this.$utils.toFixedNumber(this.selectedTask.originalMuFinish, 1)
            : null
          break
        case 'pack':
          unsplitTaskUpdates.muPack = this.selectedTask?.originalMuPack
            ? this.$utils.toFixedNumber(this.selectedTask.originalMuPack, 1)
            : null
          break
        case 'dispatch':
          unsplitTaskUpdates.muDispatch = this.selectedTask?.originalMuDispatch
            ? this.$utils.toFixedNumber(this.selectedTask.originalMuDispatch, 1)
            : null
          break
      }

      await updateDoc(doc(db, 'tasks', this.selectedTask.id), unsplitTaskUpdates)

      this.selectedTask = null
      this.taskDialog = false
    },
    async generateRequiredDays(queryRange, force) {
      const batch = writeBatch(db)
      let writes = 0

      for (var i = 0; i < this.calendarDuration; i++) {
        let momentDay = this.$moment(this.activeDateRange.start).add(i, 'days')
        let dayCapacity = this.capacity.find((o) => {
          return momentDay.isSame(o.date.seconds * 1000, 'day')
        })

        // save default capacity if doesn't exist in DB
        if (!dayCapacity && momentDay.isBetween(queryRange[0], queryRange[1], undefined, '[]')) {
          let capacityData = this.getDaysBaseCapacityFromSchedules(momentDay)
          capacityData.dateCreated = this.$utils.tsNow()
          capacityData.createdBy = this.$store.state.user.email

          const capacityRef = doc(collection(db, 'capacity'))
          batch.set(capacityRef, capacityData)
          writes++
        } else if (force && momentDay.isBetween(queryRange[0], queryRange[1], undefined, '[]')) {
          let capacityData = this.getDaysBaseCapacityFromSchedules(momentDay)
          capacityData.dateCreated = this.$utils.tsNow()
          capacityData.createdBy = this.$store.state.user.email

          let capacityForDay = this.capacity.find((o) => {
            return momentDay.isSame(this.$moment.unix(o.date.seconds), 'day')
          })
          const capacityRef = doc(db, 'capacity', capacityForDay.id)
          batch.update(capacityRef, capacityData)
          writes++
        }
      }

      if (!writes) {
        return true
      }

      await batch.commit()
      return true
    },
    async updateHolidays() {
      this.holidaysLoading = true
      try {
        const getHolidays = httpsCallable(functions, 'getHolidays')
        const holidays = await getHolidays({
          startDate: this.$moment(this.activeDateRange.start).format('YYYY-MM-DD'),
          endDate: this.$moment(this.activeDateRange.end).format('YYYY-MM-DD')
        })
        this.timetasticHolidays = holidays.data.holidays
        this.holidayPreviewMode = true
      } catch (e) {
        console.error(e)
        this.$store.commit('openSnackbar', {
          snackbarText: 'Failed to fetch holidays'
        })
      } finally {
        this.holidaysLoading = false
      }
    },
    async updateCapacity(force) {
      this.capacityLoaded = false

      // Set query range as a const for use after initial snap
      const startTimestamp = this.$utils.timestampFromVuetify(this.activeDateRange.start, 'start')
      const endTimestamp = this.$utils.timestampFromVuetify(this.activeDateRange.end, 'end')
      const queryRange = [this.$moment(this.activeDateRange.start), this.$moment(this.activeDateRange.end)]

      const capacityQuery = query(
        collection(db, 'capacity'),
        where('date', '>=', startTimestamp),
        where('date', '<=', endTimestamp),
        orderBy('date')
      )

      const existing = await getDocs(capacityQuery)
      this.capacity = existing.docs.map((capacitySnap) => serializeSnapshot(capacitySnap))

      await this.generateRequiredDays(queryRange, force)

      // Now setup a listener for ongoing changes to data
      await this.$live('capacity', capacityQuery)
      // this.updateHolidays(this.capacity) // DISABLED UNTIL FIX IS MADE!
      this.capacityLoaded = true
    },
    getDaysBaseCapacityFromSchedules(momentDay) {
      let working = []
      let notWorking = []

      // staff is null until App.vue's snapshot lands
      const employed = (this.$store.state.staff || []).filter((o) => {
        return o.staffType === 'employed' && !o.departureDate
      })

      // Track whether staff member is working or not working
      for (const s of employed) {
        const staffScheduleForDate = this.$store.getters.staffHoursByMoment(momentDay, s)
        if (staffScheduleForDate) {
          const hours = staffScheduleForDate[momentDay.isoWeekday() - 1]
          if (hours > 0) {
            working.push({
              name: s.name,
              timetasticId: s.timetasticId,
              hours
            })
          } else {
            notWorking.push({
              name: s.name,
              timetasticId: s.timetasticId
            })
          }
        }
      }

      return {
        date: this.$utils.timestampFromMoment(momentDay),
        working,
        holidays: [],
        sick: [],
        overtime: [],
        freelancers: []
      }
    },

    paginateCalendar(direction) {
      var increment = this.selectedCalendarView == 'custom-daily' ? 'week' : this.selectedCalendarView
      if (direction == 'next') {
        this.today = this.$moment(this.today).add(1, increment).format('YYYY-MM-DD')
      } else {
        this.today = this.$moment(this.today).subtract(1, increment).format('YYYY-MM-DD')
      }

      this.loadTasks()
      this.updateCapacity()
    },
    resetPagination() {
      this.today = this.$moment().format('YYYY-MM-DD')
      this.loadTasks()
      this.updateCapacity()
    },
    async loadUnscheduledTasks() {
      // Carried maxRefDepth: 0 for the same reason as loadTasks — see the note there.
      await this.$live('unscheduledTasks', query(collection(db, 'tasks'), where('dateScheduled', '==', null)))
      this.loadShipments()
    },
    unscheduledShipmentColour(tasks) {
      // Handle empty or invalid tasks array
      if (!tasks || tasks.length === 0) return 'default'

      const firstTask = tasks[0]
      const shipmentID = firstTask.shipmentID

      // Handle manual tasks without shipmentID
      if (!shipmentID) {
        if (firstTask.manuallyCreated) return 'bg-primary'
        if (firstTask.complete) return 'bg-success'
        return 'default'
      }

      // Handle paused tasks
      if (firstTask.paused) return 'paused'

      // Check if we have the shipment loaded
      const shipment = this.shipmentsByID[shipmentID]
      if (shipment) {
        // Determine color based on toolpathed and materialOrdered status
        if (shipment.toolpathed && shipment.materialOrdered) return 'bg-info'
        if (shipment.toolpathed && !shipment.materialOrdered) return 'bg-success'
        if (!shipment.toolpathed && !shipment.materialOrdered) return 'bg-error'
        if (!shipment.toolpathed && shipment.materialOrdered) return 'rare'
      }

      if (!shipment) return 'bg-warning'

      // Default fallback
      if (firstTask.complete) return 'bg-success'
      return 'bg-warning'
    },
    isTaskPropertyTrue(shipmentID, property) {
      const shipment = this.shipmentsByID[shipmentID]
      if (!shipment || shipment[property] == undefined) {
        return false // Assume false if the shipment is not loaded or the property is unset
      } else {
        return shipment[property]
      }
    },
    // Every shipment ID referenced by a task currently on the board, scheduled or not.
    referencedShipmentIDs() {
      const ids = new Set()
      for (const task of this.tasks) if (task.shipmentID) ids.add(task.shipmentID)
      for (const task of this.unscheduledTasks) if (task.shipmentID) ids.add(task.shipmentID)
      return [...ids].sort()
    },
    loadShipments() {
      const ids = this.referencedShipmentIDs()
      const key = ids.join(',')
      if (key === this._boundShipmentKey) return
      this._boundShipmentKey = key
      let pending = 0

      this._shipmentChunkUnsubs.forEach((unsub) => unsub())
      this._shipmentChunkUnsubs = []

      if (!ids.length) {
        this._shipmentChunks = []
        this.shipments = []
        this.tasksBounded = true
        return
      }

      // documentId() 'in' takes at most 30 values per query.
      const chunks = []
      for (let i = 0; i < ids.length; i += 30) chunks.push(ids.slice(i, i + 30))

      this._shipmentChunks = chunks.map(() => [])
      pending = chunks.length

      chunks.forEach((chunkIDs, index) => {
        this._shipmentChunkUnsubs.push(
          onSnapshot(
            query(collection(db, 'shipments'), where(documentId(), 'in', chunkIDs)),
            (snap) => {
              this._shipmentChunks[index] = snap.docs.map((shipmentSnap) => serializeSnapshot(shipmentSnap))
              this.shipments = this._shipmentChunks.flat()
              this.tasksBounded = true
              if (pending > 0 && --pending === 0) stop(`${ids.length} shipments in ${chunks.length} chunk(s)`)
            },
            (error) => {
              console.error('shipments listener failed:', error)
            }
          )
        )
      })
    },
    isShipmentPropertyTrue(shipmentID, property) {
      // Look up the shipment directly from loaded shipments
      const shipment = this.shipmentsByID[shipmentID]
      return shipment ? shipment[property] : false
    },
    updateTasksByDay() {
      // create the array
      var o = {}
      let i
      for (i = 0; i <= this.calendarDuration - 1; i++) {
        o[this.$moment(this.activeDateRange.start).add(i, 'days').format('YYYY-MM-DD')] = []
      }
      // add the tasks
      for (let task of this.tasks) {
        var date = this.$utils.vuetifyTimestamp(task.dateScheduled)
        if (o[date]) {
          o[date].push(task)
        }
      }

      // sort each array by index
      var dates = Object.keys(o)
      for (i = 0; i < dates.length; i++) {
        var dateArray = o[dates[i]]
        dateArray.sort((a, b) => {
          return (a.index ?? 0) - (b.index ?? 0)
        })
      }

      this.tasksByDay = o
    },
    updateUnscheduledByShipment() {
      // convert to a keyed object
      var o = this.$utils.groupArrayByKey(this.unscheduledTasks, 'shipmentID')
      // convert back into an array
      var a = Object.values(o)

      // Filter out manual tasks
      var manualTasksArray = []
      var regularTasks = []

      a.forEach((tasks) => {
        if (tasks.length > 0) {
          const firstTask = tasks[0]
          if (firstTask.manuallyCreated === true && firstTask.shipmentID === null) {
            // Add all individual manual tasks to the array
            manualTasksArray.push(...tasks)
          } else {
            regularTasks.push(tasks)
          }
        }
      })

      regularTasks.sort((a, b) => {
        const aTime = a[0]?.targetDeliveryDate?.seconds
        const bTime = b[0]?.targetDeliveryDate?.seconds

        if (aTime == null && bTime == null) return 0
        if (aTime == null) return -1
        if (bTime == null) return 1

        return aTime - bTime
      })

      // sort manual tasks by target delivery date as well
      manualTasksArray.sort((a, b) => {
        const aTime = a?.targetDeliveryDate?.seconds
        const bTime = b?.targetDeliveryDate?.seconds

        if (aTime == null && bTime == null) return 0
        if (aTime == null) return -1
        if (bTime == null) return 1

        return aTime - bTime
      })

      // set the arrays
      this.manualTasks = manualTasksArray
      this.unscheduledByShipment = [...regularTasks]

      if (!this.hasLoadedScheduleDashboard) {
        this.processingShipments = regularTasks // Start with regular shipments in the first card
        this.jobsToScheduleShipments = []
        this.readyForDispatchShipments = []
      }
    },
    async validate() {
      if (!(await this.$utils.validateForm(this.$refs.form))) return
      if (this.submitting) return
      this.submitting = true

      this.createNewTask()
    },
    createNewTask() {
      this.form.dateCreated = this.$utils.tsNow()
      this.form.dateScheduled = null
      this.form.shipmentID = null
      this.form.shipmentRef = null
      this.form.targetDeliveryDate = this.$utils.tsNow()
      this.form.manuallyCreated = true
      this.form.complete = null
      this.form.inProduction = true

      // clean up
      if (!this.form.subtype) {
        delete this.form.subtype
      }
      if (!this.form.objectType) {
        delete this.form.objectType
      }

      // assign mu to the correct task type
      if (this.form.workMinutes && this.form.mu && this.form.type) {
        switch (this.form.type) {
          case 'cut':
            this.form.muCut = this.form.mu
            break
          case 'finish':
          case 'assemble':
            this.form.muFinish = this.form.mu
            break
          case 'pack':
            this.form.muPack = this.form.mu
            break
          case 'dispatch':
            this.form.muDispatch = this.form.mu
            break
          default:
            this.form.muUnits = this.form.mu
            break
        }
      }

      addDoc(collection(db, 'tasks'), this.form)
        .then(() => {
          this.newTaskDialog = false
          this.submitting = false
          this.$refs.form.reset()
        })
        .catch((err) => {
          console.log('Error', err)
        })
    },
    openPackingView(shipmentID) {
      var routeData = this.$router.resolve({
        name: 'packingView',
        params: { shipmentID: shipmentID }
      })
      window.open(routeData.href, '_blank')
    },
    openSprayerView(shipmentID) {
      var routeData = this.$router.resolve({
        name: 'sprayerView',
        params: { shipmentID: shipmentID }
      })
      window.open(routeData.href, '_blank')
    },
    openShipmentInfo(tasks) {
      this.selectedShipmentInfo = tasks
      this.shipmentInfoDialog = true
    },
    getShipmentMaterialDueIn(shipmentID) {
      const shipment = this.shipments.find((s) => s.id === shipmentID)
      if (!shipment || !shipment.materialOrdered) return 'Not set'
      return this.$moment(shipment.materialOrdered.toDate()).format('DD MMM YYYY')
    },
    openQuoteView(shipmentID) {
      const shipment = this.shipments.find((s) => s.id === shipmentID)
      if (!shipment) return
      var routeData = this.$router.resolve({
        name: 'quoteEdit',
        params: { quoteID: shipment.quoteID, versionID: shipment.versionID }
      })
      window.open(routeData.href, '_blank')
    },
    openMaterialView(shipmentID) {
      const shipment = this.shipments.find((s) => s.id === shipmentID)
      if (!shipment) return
      this.materialViewShipment = shipment
      this.materialViewDialog = true
    },
    printableSVG(shipmentID) {
      const shipment = this.shipments.find((s) => s.id === shipmentID)
      if (!shipment) {
        console.error('printableSVG: no shipment found for', shipmentID)
        return
      }
      var routeData = this.$router.resolve({
        name: 'customerSVG',
        params: {
          quoteID: shipment.quoteID,
          versionID: shipment.versionID
        }
      })
      window.open(routeData.href, '_blank')
    },
    focusTask(task) {
      if (this.focussedTask && this.focussedTask.shipmentID == task.shipmentID) {
        this.focussedTask = null
      } else {
        this.focussedTask = task
      }
    },
    onTaskDialogClose() {
      this.displayDispatchEtaPicker = false
      this.tmpOverideDispatchEta = false

      if (this.selectedTask.notes !== this.tmpValue) {
        updateDoc(doc(db, 'tasks', this.selectedTask.id), {
          notes: this.selectedTask.notes
        }).then(() => {
          this.$store.commit('openSnackbar', {
            snackbarText: 'Task notes updated'
          })
        })
      }
    },
    // handle shipment reordering when expansion panels are dragged
    async onShipmentDragEnd() {
      // Save the new order to Firestore
      await this.saveScheduleDashboard()
    },
    // save the current schedule dashboard order to Firestore
    async saveScheduleDashboard() {
      try {
        // Validate arrays exist before processing
        const manual = this.manualTasks || []
        const processing = this.processingShipments || []
        const jobsToSchedule = this.jobsToScheduleShipments || []
        const readyForDispatch = this.readyForDispatchShipments || []

        const scheduleData = {
          manual: this.buildManualTasksArray(manual),
          processing: this.buildScheduleArray(processing),
          jobsToSchedule: this.buildScheduleArray(jobsToSchedule),
          readyForDispatch: this.buildScheduleArray(readyForDispatch)
        }

        // Only save if we have valid data
        if (
          scheduleData.manual.length > 0 ||
          scheduleData.processing.length > 0 ||
          scheduleData.jobsToSchedule.length > 0 ||
          scheduleData.readyForDispatch.length > 0
        ) {
          await setDoc(doc(db, 'schedule', 'scheduleDashboard'), scheduleData)
        } else {
          // If all arrays are empty, delete the document
          await deleteDoc(doc(db, 'schedule', 'scheduleDashboard'))
        }
      } catch (error) {
        console.error('Error saving schedule dashboard:', error)
      }
    },
    // helper method to build manual tasks array
    buildManualTasksArray(tasksArray) {
      return tasksArray.map((task, index) => ({
        taskId: task.id,
        taskIds: [task.id], // Add taskIds array for consistency
        scheduleDashOrder: index
      }))
    },
    // helper method to build schedule arrays with shipment references
    buildScheduleArray(shipmentsArray) {
      const filtered = shipmentsArray.filter((tasks) => {
        const isValid = tasks && tasks.length > 0
        return isValid
      })

      const result = filtered.map((tasks, index) => ({
        shipmentID: tasks[0].shipmentID,
        taskId: tasks[0].id, // For manual tasks without shipmentID
        taskIds: tasks.map((task) => task.id), // Add array of all task IDs for reactive panels
        scheduleDashOrder: index
      }))
      return result
    },
    // load saved schedule dashboard order from Firestore
    async loadScheduleDashboard() {
      try {
        const snap = await getDoc(doc(db, 'schedule', 'scheduleDashboard'))
        if (snap.exists()) {
          const data = snap.data()

          // Apply the saved order to the shipment arrays
          await this.applyScheduleOrder(data)
          this.hasLoadedScheduleDashboard = true
        } else {
          // No saved data exists, use default organization
          this.hasLoadedScheduleDashboard = false
        }
      } catch (error) {
        console.error('Error loading schedule dashboard:', error)
        this.hasLoadedScheduleDashboard = false
      }
    },
    // apply the saved schedule order to the current shipment arrays
    async applyScheduleOrder(scheduleData) {
      // Wait for unscheduled tasks to be loaded first
      if (this.unscheduledByShipment.length === 0) {
        // If not loaded yet, wait a bit and try again
        setTimeout(() => this.applyScheduleOrder(scheduleData), 500)
        return
      }

      // Create lookup map of shipmentID to tasks array
      const shipmentMap = {}
      const taskMap = {} // For manual tasks without shipmentID
      this.unscheduledByShipment.forEach((tasks) => {
        if (tasks.length > 0) {
          if (tasks[0].shipmentID) {
            shipmentMap[tasks[0].shipmentID] = tasks
          } else {
            // For manual tasks without shipmentID, use task ID
            taskMap[tasks[0].id] = tasks
          }
        }
      })

      // Rebuild manual tasks array based on saved order
      this.manualTasks = this.buildManualTasksArrayFromSchedule(scheduleData.manual || [], taskMap)
      this.processingShipments = this.buildShipmentArrayFromSchedule(
        scheduleData.processing || [],
        shipmentMap,
        taskMap
      )
      this.jobsToScheduleShipments = this.buildShipmentArrayFromSchedule(
        scheduleData.jobsToSchedule || [],
        shipmentMap,
        taskMap
      )
      this.readyForDispatchShipments = this.buildShipmentArrayFromSchedule(
        scheduleData.readyForDispatch || [],
        shipmentMap,
        taskMap
      )

      // Ensure any unscheduled, non-manual groups without shipmentID are added to processing
      this.unscheduledByShipment.forEach((tasks) => {
        if (!tasks || tasks.length === 0) return
        const first = tasks[0]
        const isManual = first.manuallyCreated === true && first.shipmentID === null
        const hasShipment = !!first.shipmentID
        if (!isManual && !hasShipment) {
          const alreadyInProcessing = this.processingShipments.find((group) => group[0]?.id === first.id)
          if (!alreadyInProcessing) {
            this.processingShipments.push(tasks)
          }
        }
      })

      // Add any shipmentID groups not present in any category into processing
      const includedShipmentIds = new Set([
        ...this.processingShipments.map((g) => g[0]?.shipmentID).filter(Boolean),
        ...this.jobsToScheduleShipments.map((g) => g[0]?.shipmentID).filter(Boolean),
        ...this.readyForDispatchShipments.map((g) => g[0]?.shipmentID).filter(Boolean)
      ])
      this.unscheduledByShipment.forEach((tasks) => {
        if (!tasks || tasks.length === 0) return
        const first = tasks[0]
        if (first.shipmentID && !includedShipmentIds.has(first.shipmentID)) {
          this.processingShipments.push(tasks)
          includedShipmentIds.add(first.shipmentID)
        }
      })

      // Update the original unscheduledByShipment to reflect the new organization
      this.unscheduledByShipment = [
        ...this.processingShipments,
        ...this.jobsToScheduleShipments,
        ...this.readyForDispatchShipments
      ]
    },
    // helper method to build manual tasks array from schedule data
    buildManualTasksArrayFromSchedule(scheduleArray, taskMap) {
      return scheduleArray
        .sort((a, b) => a.scheduleDashOrder - b.scheduleDashOrder)
        .map((item) => taskMap[item.taskId])
        .filter((task) => task) // Remove any undefined entries
    },
    // helper method to build shipment array from schedule data
    buildShipmentArrayFromSchedule(scheduleArray, shipmentMap, taskMap = {}) {
      return scheduleArray
        .sort((a, b) => a.scheduleDashOrder - b.scheduleDashOrder)
        .map((item) => {
          // Try shipmentID first, then taskId for manual tasks
          return shipmentMap[item.shipmentID] || taskMap[item.taskId]
        })
        .filter((tasks) => tasks) // Remove any undefined entries
    },
    // save changes back to database when task moved
    async onSortEnd(e) {
      const dropNode = e.item || e.clone
      if (dropNode) {
        if (dropNode.__scheduleDropHandled) return
        dropNode.__scheduleDropHandled = true
        setTimeout(() => delete dropNode.__scheduleDropHandled, 0)
      }

      try {
        const finalTaskId = e.item?.id || e.clone?.id

        // Store it in the event object so it doesn't get lost
        e._capturedTaskId = finalTaskId

        if (!finalTaskId) {
          console.error('onSortEnd: No task ID found')
          return
        }

        const movedTask = this.findTaskById(finalTaskId)

        const fromId = e.from.id
        const toId = e.to.id
        const fromUnscheduled = fromId === 'unscheduled' || fromId === 'dropzone'
        const toUnscheduled = toId === 'unscheduled' || toId === 'dropzone'

        // Collect a single merged update per task so each doc is written only once
        const updates = new Map()
        const queueUpdate = (id, data) => updates.set(id, { ...(updates.get(id) || {}), ...data })

        // Move task between days
        if (e.to !== e.from) {
          const date = toUnscheduled ? null : toId
          const dateScheduled = this.$utils.timestampFromVuetify(date)
          queueUpdate(finalTaskId, { dateScheduled })
          if (movedTask) movedTask.dateScheduled = dateScheduled

          if (date === null) {
            // Moving to unscheduled - clear the index
            queueUpdate(finalTaskId, { index: null })
          } else if (fromUnscheduled) {
            // Optimistically remove from unscheduled arrays to prevent duplicate key warning
            this.unscheduledTasks = this.unscheduledTasks.filter((task) => task.id !== finalTaskId)
            this.manualTasks = this.manualTasks.filter((task) => task.id !== finalTaskId)
            // Also remove from shipment arrays
            this.removeTaskFromShipmentArrays(finalTaskId)
          }
        }

        if (!toUnscheduled) {
          this.queueDayIndexes(queueUpdate, toId, e.to, {
            insertTask: movedTask,
            insertAt: e.newIndex,
            // A same-day move has to leave its old slot before it can take the new one.
            removeId: fromId === toId ? finalTaskId : null
          })
        }
        if (!fromUnscheduled && fromId !== toId) {
          this.queueDayIndexes(queueUpdate, fromId, e.from, { removeId: finalTaskId })
        }

        const batch = writeBatch(db)
        for (const [taskId, data] of updates) {
          batch.update(doc(db, 'tasks', taskId), data)
        }

        await batch.commit()

        // Check if any shipments became empty after task movement and update schedule dashboard
        await this.handleShipmentChangesAfterTaskMove(e)
      } catch (error) {
        console.error('onSortEnd: Error occurred', error)
        console.error('onSortEnd: Error stack', error.stack)
      } finally {
        if (e.to?.id === 'dropzone' && this.dropzoneTasks.length) {
          try {
            await this.handleDropzoneDrop(e)
          } catch (error) {
            console.error('onSortEnd: dropzone drain failed', error)
          }
        }
      }
    },
    queueDayIndexes(queueUpdate, date, container, { insertTask, insertAt, removeId } = {}) {
      const dayTasks = this.tasksByDay[date] || []

      // No attribute (month view) means the container holds every type
      const laneTypesAttr = container.dataset ? container.dataset.laneTypes : null
      const laneTypes = laneTypesAttr ? laneTypesAttr.split(',') : null
      const inLane = (task) => !laneTypes || laneTypes.indexOf(task.type) >= 0

      const laneTasks = dayTasks.filter(inLane)

      if (removeId) {
        const at = laneTasks.findIndex((task) => task.id === removeId)
        if (at >= 0) laneTasks.splice(at, 1)
      }
      if (insertTask && inLane(insertTask)) {
        const existing = laneTasks.findIndex((task) => task.id === insertTask.id)
        if (existing >= 0) laneTasks.splice(existing, 1)
        const at = insertAt == null ? laneTasks.length : Math.min(insertAt, laneTasks.length)
        laneTasks.splice(at, 0, insertTask)
      }

      const ordered = []
      let i = 0
      for (const task of dayTasks) {
        if (inLane(task)) {
          if (i < laneTasks.length) ordered.push(laneTasks[i++])
        } else {
          ordered.push(task)
        }
      }
      while (i < laneTasks.length) ordered.push(laneTasks[i++])

      ordered.forEach((task, index) => {
        queueUpdate(task.id, { index })
        task.index = index
      })

      this.tasksByDay[date] = ordered
    },
    // handle tasks dropped in the dropzone
    async handleDropzoneDrop(e) {
      try {
        // Get the task that was dropped
        const taskId = e.item?.id || e.clone?.id
        const droppedTask = this.dropzoneTasks.find((task) => task.id === taskId)

        this.dropzoneTasks = []

        if (droppedTask) {
          // Determine target category using the new logic
          const targetCategory = this.determineTargetCategory(e.to, taskId)

          // Find existing shipment panel or create new one
          const existingPanel = this.findExistingShipmentPanel(droppedTask.shipmentID, taskId)

          if (existingPanel) {
            // Add task to existing panel and update taskIds array
            if (!existingPanel.panel.find((task) => task.id === taskId)) {
              existingPanel.panel.push(droppedTask)
            }
          } else {
            // Create new shipment panel in the target category
            const newPanel = [droppedTask]

            switch (targetCategory) {
              case 'processing':
                this.processingShipments.push(newPanel)
                break
              case 'jobsToSchedule':
                this.jobsToScheduleShipments.push(newPanel)
                break
              case 'readyForDispatch':
                this.readyForDispatchShipments.push(newPanel)
                break
              case 'manual':
                this.manualTasks.push(droppedTask)
                break
              default:
                this.processingShipments.push(newPanel)
            }
          }

          // Pick up the shipment if this task brought a new one onto the board
          this.loadShipments()

          // Update the unscheduled data
          this.updateUnscheduledByShipment()
          await this.saveScheduleDashboard()
        }
      } catch (error) {
        console.error('Error handling dropzone drop:', error)
      }
    },
    // handle changes to shipments when tasks are moved between scheduled and unscheduled
    async handleShipmentChangesAfterTaskMove(e) {
      // Use the captured task ID to avoid timing issues
      const taskId = e._capturedTaskId || e.item?.id || e.clone?.id

      // Check if task was moved to/from unscheduled area
      const movedToScheduled = e.from.id === 'unscheduled' && e.to.id !== 'unscheduled'
      const movedToUnscheduled = e.from.id !== 'unscheduled' && e.to.id === 'unscheduled'

      if (movedToScheduled || movedToUnscheduled) {
        // If task was moved back to unscheduled, update its dateScheduled to null
        if (movedToUnscheduled && taskId) {
          try {
            await updateDoc(doc(db, 'tasks', taskId), {
              dateScheduled: null
            })
          } catch (error) {
            console.error('Error updating task dateScheduled to null:', error)
          }
        }

        // Refresh unscheduled data to get updated task lists
        this.updateUnscheduledByShipment()

        // Wait a moment for data to update
        setTimeout(async () => {
          // Remove empty shipments from all categories
          this.removeEmptyShipments()

          // Pick up the shipment if a moved task brought a new one onto the board
          this.loadShipments()

          // Update the schedule dashboard
          await this.saveScheduleDashboard()
        }, 200)
      }
    },
    removeEmptyShipments() {
      const occupied = (tasks) => Array.isArray(tasks) && tasks.length > 0
      this.manualTasks = this.manualTasks.filter((task) => task)
      this.processingShipments = this.processingShipments.filter(occupied)
      this.jobsToScheduleShipments = this.jobsToScheduleShipments.filter(occupied)
      this.readyForDispatchShipments = this.readyForDispatchShipments.filter(occupied)
    },
    // determine which category card the task was dropped into
    determineTargetCategory(dropTarget, taskId) {
      // First, try to find an existing shipment panel that matches this task
      const task = this.findTaskById(taskId)
      if (!task) return 'processing' // Default fallback

      const shipmentID = task.shipmentID

      // Look for existing shipment panels in each category
      const existingPanel = this.findExistingShipmentPanel(shipmentID, taskId)
      if (existingPanel) {
        return existingPanel.category
      }

      // If no existing panel found, walk up the DOM tree to find the parent card
      let element = dropTarget
      while (element && element.parentElement) {
        // Check for category-specific classes or data attributes
        if (element.closest) {
          // Look for the draggable container within each card
          const processingCard = element.closest('.processing-card')
          const scheduleCard = element.closest('.jobs-to-schedule-card')
          const dispatchCard = element.closest('.ready-for-dispatch-card')

          if (processingCard) return 'processing'
          if (scheduleCard) return 'jobsToSchedule'
          if (dispatchCard) return 'readyForDispatch'
        }
        element = element.parentElement
      }

      return 'processing' // Default to processing if no specific category found
    },
    // find a task by its ID from all available task sources
    findTaskById(taskId) {
      // Check in unscheduledTasks first
      let task = this.unscheduledTasks.find((task) => task.id === taskId)
      if (task) return task

      // Check in tasksByDay
      for (const dayTasks of Object.values(this.tasksByDay)) {
        task = dayTasks.find((task) => task.id === taskId)
        if (task) return task
      }

      // Check in dropzoneTasks
      task = this.dropzoneTasks.find((task) => task.id === taskId)
      if (task) return task

      return null
    },
    // find existing shipment panel across all categories
    findExistingShipmentPanel(shipmentID, taskId) {
      // Check processing shipments
      let panel = this.processingShipments.find((tasks) => tasks.length > 0 && tasks[0].shipmentID === shipmentID)
      if (panel) return { panel, category: 'processing' }

      // Check jobs to schedule shipments
      panel = this.jobsToScheduleShipments.find((tasks) => tasks.length > 0 && tasks[0].shipmentID === shipmentID)
      if (panel) return { panel, category: 'jobsToSchedule' }

      // Check ready for dispatch shipments
      panel = this.readyForDispatchShipments.find((tasks) => tasks.length > 0 && tasks[0].shipmentID === shipmentID)
      if (panel) return { panel, category: 'readyForDispatch' }

      // For manual tasks without shipmentID, check by taskId
      if (!shipmentID) {
        panel = this.manualTasks.find((task) => task.id === taskId)
        if (panel) return { panel, category: 'manual' }
      }

      return null
    },
    async resourcesChanged(date) {
      const obj = this.capacity.find((o) => o.date.seconds === date)
      const data = this.capacityObject[this.$moment.unix(date).format('YYYY-MM-DD')]
      if (obj && data) {
        await updateDoc(doc(db, 'capacity', obj.id), data)
      }
    },
    saveReset() {
      if (this.$refs.activeView && this.$refs.activeView.applyReset) {
        this.$refs.activeView.applyReset()
      }
    },
    saveHolidayUpdate() {
      if (this.$refs.activeView && this.$refs.activeView.applyHolidayUpdate) {
        this.$refs.activeView.applyHolidayUpdate()
      }
    },
    cutHoursChanged(date, cutHours) {
      const dateKey = this.$moment.unix(date).format('YYYY-MM-DD')
      if (this.capacityObject[dateKey]) {
        this.capacityObject[dateKey].cutting = cutHours
      }
      this.resourcesChanged(date)
    }
  }
}
</script>
<style lang="scss" scoped>
#view-sub-nav {
  // == Vuetify's `button` typography size, i.e. exactly what the Today button renders at.
  $toolbar-font-size: 0.875rem;

  :deep(.v-switch .v-label) {
    font-size: $toolbar-font-size;
  }

  :deep(.v-select .v-field) {
    font-size: $toolbar-font-size;
  }

  .view-select {
    flex: 0 0 140px;
  }
}

.repeat-task-dialog {
  .heading {
    font-size: 1.5rem !important;
  }
}

.task-dialog {
  .action {
    .v-btn {
      &:first-of-type {
        margin-left: 0;
      }
    }
  }
}

#schedule {
  flex-grow: 1;
  width: 100%;
  display: flex;
  flex-direction: column;

  #schedule-body {
    width: 100%;
    flex-grow: 1;
    display: flex;

    #calendar-container {
      flex-grow: 1;
      position: relative;
    }
    #unscheduled-container {
      width: 0%;
      transition: width 0.5s;
      overflow: hidden;
      background: #fafafa;
      border-right: solid 1px #e0e0e0;
      box-shadow: inset -1px 0px 5px 0px rgba(0, 0, 0, 0.2);
      display: flex;
      flex-direction: column;

      #unscheduled {
        height: auto;
        min-height: 0;
      }

      .draggable-list {
        height: auto !important;
        min-height: 0;
        flex-shrink: 0;
        flex-grow: 0 !important;
      }

      :deep(.task) {
        padding-top: 8px;
        padding-bottom: 8px;
      }

      :deep(.task .v-icon--clickable) {
        color: inherit;

        &:hover {
          color: inherit;
          opacity: 0.7;
        }
      }

      &.open {
        width: 20%;
      }

      ::v-deep.v-expansion-panel {
        margin: 0 !important;
        width: 100% !important;
        .v-expansion-panel-text__wrapper {
          padding: 2px;
        }
        .v-expansion-panel-text {
          background: $sand;
          border-top: solid 1px $ruleGrey;
        }
        .v-expansion-panel-title {
          width: 100% !important;
        }
      }

      // Visual feedback for drag and drop
      .shipment-panel {
        position: relative;

        &.drag-over-invalid {
          background-color: rgba(255, 0, 0, 0.1);
          border: 2px dashed #ff0000;
        }
      }

      .processing-card,
      .jobs-to-schedule-card,
      .ready-for-dispatch-card {
        position: relative;

        &.drag-over-valid {
          background-color: rgba(0, 255, 0, 0.1);
          border: 2px dashed #00ff00;
        }
      }
      :deep(.v-expansion-panel-title) {
        padding: 0 4px 0 8px;
        &.paused {
          background: repeating-linear-gradient(-45deg, #e2602d, #e2602d 10px, #dadada 10px, #dadada 20px) !important;
          outline: solid 2px #f85818;
        }
        &.rare {
          background: $rare;
        }
      }

      .info-icon {
        color: white !important;
        cursor: pointer;
      }

      :deep(.v-expansion-panel-title.default),
      :deep(.v-expansion-panel-title.paused) {
        .info-icon {
          color: $idleGrey !important;
        }
      }
    }
  }
}

::v-deep .header {
  .past {
    opacity: 0.4;
  }
  .present {
    color: $pinkLink;
  }
}

::v-deep .task {
  margin: 4px;
  padding: 4px;
  border-radius: 3px;
  cursor: pointer;
  background: #eee;
  display: flex;
  &.sortable-ghost,
  &.ghost {
    opacity: 0.2;
  }
  &.inverted {
    background: white;
  }
  &.paused {
    background: repeating-linear-gradient(-45deg, #e2602d, #e2602d 10px, #dadada 10px, #dadada 20px) !important;
    outline: solid 2px #f85818;
  }
}

// Dropzone styles
.dropzone-container {
  position: sticky;
  bottom: 0;
  padding: 8px;
  margin-top: auto;
  background: #fafafa;
  border-top: 1px solid #e0e0e0;
  z-index: 10;
}

.small-dropzone {
  height: 60px;
  border: 2px dashed #ccc;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #fafafa;
  transition: all 0.3s ease;
  cursor: pointer;

  &:hover {
    border-color: #999;
    background-color: #f5f5f5;
  }

  .dropzone-content {
    text-align: center;
    color: #666;

    .v-icon {
      color: #999;
    }
  }
}
</style>
